<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { routePlansApi, type RoutePlan } from '@/api/route-plans';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import TimeInput from '@/components/time-input.vue';
import { strandedAssignments, windowValid } from './route-caregiver-edit.utils';
import {
  DEFAULT_LUNCH_DURATION_MINUTES, LUNCH_DURATION_OPTIONS,
} from './route-plan-setup.utils';

// Opened by clicking a caregiver's name on the board calendar: edits that professional's
// working window (and lunch, when the plan has one) in place. Saving never re-optimizes —
// placements outside the new window stay put and the dialog warns so the user moves them.
interface Props {
  modelValue: boolean;
  routePlan: RoutePlan;
  routePlanCaregiverId: number | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'saved': [warnings: string[]];
}>();

const { t } = useI18n();
const HM_LENGTH = 5;

const availableFrom = ref('');
const availableTo = ref('');
const lunchStart = ref('');
const lunchTouched = ref(false);
const lunchDuration = ref(DEFAULT_LUNCH_DURATION_MINUTES);
const lunchDurationTouched = ref(false);
const busy = ref(false);
const errorText = ref('');

const routePlanCaregiver = computed(() => props.routePlan.routePlanCaregivers
  .find(rpc => rpc.id === props.routePlanCaregiverId) ?? null);

// The serializer doesn't expose lunch_start; the plan's break map (keyed by caregiverId) is
// the only source. No entry means the plan has no lunch for them — the field stays hidden.
const currentBreak = computed(() => {
  const caregiverId = routePlanCaregiver.value?.caregiverId;

  return caregiverId ? props.routePlan.caregiverBreaks[caregiverId] ?? null : null;
});

function hm(value: string): string {
  const start = value.includes('T') ? value.indexOf('T') + 1 : 0;

  return value.slice(start, start + HM_LENGTH);
}

function reset() {
  errorText.value = '';
  lunchTouched.value = false;
  lunchDurationTouched.value = false;
  availableFrom.value = routePlanCaregiver.value ? hm(routePlanCaregiver.value.availableFrom) : '';
  availableTo.value = routePlanCaregiver.value ? hm(routePlanCaregiver.value.availableTo) : '';
  lunchStart.value = currentBreak.value ? hm(currentBreak.value.startTime) : '';
  lunchDuration.value =
    routePlanCaregiver.value?.lunchDurationMinutes ?? DEFAULT_LUNCH_DURATION_MINUTES;
}

watch(() => props.modelValue, (open) => {
  if (open) reset();
}, { immediate: true });

const validWindow = computed(() => windowValid(availableFrom.value, availableTo.value));

const stranded = computed(() => {
  const caregiverId = routePlanCaregiver.value?.caregiverId;
  if (!caregiverId || !validWindow.value) return [];

  return strandedAssignments(
    props.routePlan.routeAssignments, caregiverId, availableFrom.value, availableTo.value,
  );
});

const canSave = computed(() => routePlanCaregiver.value !== null && validWindow.value);

function requestError(error: unknown): string {
  const data = (error as { response?: { data?: { errors?: string[] } } })?.response?.data;

  return data?.errors?.length ? data.errors.join(' · ') : t('routePlan.visitForm.saveError');
}

// Lunch fields ride along only when touched, so editing hours can never move an
// untouched lunch or rewrite an untouched duration.
function savePayload() {
  return {
    availableFrom: availableFrom.value,
    availableTo: availableTo.value,
    ...(lunchTouched.value && lunchStart.value ? { lunchStart: lunchStart.value } : {}),
    ...(lunchDurationTouched.value ? { lunchDurationMinutes: lunchDuration.value } : {}),
  };
}

async function onSave() {
  if (!canSave.value || busy.value) return;
  busy.value = true;
  errorText.value = '';

  try {
    const { warnings } = await routePlansApi.updateCaregiver(
      props.routePlan.id, routePlanCaregiver.value!.id, savePayload(),
    );
    emit('saved', warnings);
    emit('update:modelValue', false);
  } catch (error) {
    errorText.value = requestError(error);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <responsive-dialog
    :model-value="modelValue"
    max-width="560"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="d-flex align-center ga-3">
        <span>
          {{ t('routePlan.editCaregiver.title',
               { name: routePlanCaregiver?.caregiverFullName ?? '' }) }}
        </span>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :aria-label="t('routePlan.cancel')"
          @click="emit('update:modelValue', false)"
        />
      </v-card-title>

      <v-card-text>
        <v-alert
          type="info"
          variant="tonal"
          density="compact"
          class="mb-6"
        >
          {{ t('routePlan.editCaregiver.subtitle') }}
        </v-alert>

        <v-row class="mt-0">
          <v-col
            cols="12"
            sm="6"
          >
            <time-input
              v-model="availableFrom"
              density="comfortable"
              :label="t('routePlan.addCaregiver.from')"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <time-input
              v-model="availableTo"
              density="comfortable"
              :label="t('routePlan.addCaregiver.to')"
              :error-messages="availableFrom && availableTo && !validWindow ?
                t('routePlan.editCaregiver.invalidWindow') : undefined"
            />
          </v-col>
        </v-row>

        <v-row
          v-if="currentBreak"
          class="mt-0"
        >
          <v-col
            cols="12"
            sm="6"
          >
            <time-input
              v-model="lunchStart"
              density="comfortable"
              :label="t('routePlan.editCaregiver.lunch')"
              @update:model-value="lunchTouched = true"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="d-flex align-center ga-2"
          >
            <span class="text-caption text-medium-emphasis">
              {{ t('routePlan.lunchDuration.label') }}
            </span>
            <v-btn-toggle
              v-model="lunchDuration"
              mandatory
              density="compact"
              color="primary"
              variant="outlined"
              divided
              @update:model-value="lunchDurationTouched = true"
            >
              <v-btn
                v-for="option in LUNCH_DURATION_OPTIONS"
                :key="option"
                :value="option"
                size="small"
              >
                {{ t(`routePlan.lunchDuration.option${option}`) }}
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <v-alert
          v-if="stranded.length"
          type="warning"
          variant="tonal"
          density="compact"
          class="mt-2"
        >
          {{ t('routePlan.editCaregiver.strandedWarning', { count: stranded.length }) }}
        </v-alert>

        <v-alert
          v-if="errorText"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          {{ errorText }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-4 pb-3 pt-0">
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :loading="busy"
          :disabled="!canSave"
          @click="onSave"
        >
          {{ t('routePlan.editCaregiver.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </responsive-dialog>
</template>
